import { useMemo } from "react";
import { FestivalLineupEntry, isActivity } from "@/lib/types";
import { timeToMinutes as rawTimeToMinutes } from "@/lib/utils";

/** Treat times before 8 AM as post-midnight (add 24h) so late-night shows sort to the bottom. */
const LATE_NIGHT_CUTOFF = 8 * 60; // 08:00
function timeToScheduleMinutes(timeStr: string): number {
  const mins = rawTimeToMinutes(timeStr);
  return mins < LATE_NIGHT_CUTOFF ? mins + 24 * 60 : mins;
}

export interface ScheduleSlot {
  /** Primary entry (first in collaborative group) */
  attraction: FestivalLineupEntry;
  /** All entries sharing this stage+time (collaborative sets) */
  attractions: FestivalLineupEntry[];
  startMinutes: number;
  endMinutes: number;
  durationMinutes: number;
  stageIndex: number;
}

export interface ScheduleData {
  stages: string[];
  mainStages: string[];
  slots: ScheduleSlot[];
  dayStartMinutes: number;
  dayEndMinutes: number;
}

function inferEndMinutes(
  startMins: number,
  sameStageSlots: { startMinutes: number }[]
): number {
  // Find next act on same stage
  const nextStart = sameStageSlots
    .map((s) => s.startMinutes)
    .filter((m) => m > startMins)
    .sort((a, b) => a - b)[0];

  if (nextStart !== undefined) {
    const gap = nextStart - startMins;
    // Short intro act (≤15min)
    if (gap <= 15) return startMins + gap;
    // Normal gap: fill minus 15min changeover
    if (gap <= 180) return startMins + gap - 15;
    // Large gap: cap at 90min (break in schedule)
    return startMins + 90;
  }

  // Last act on stage: 90min default, cap at 28:00 (4am next day)
  return Math.min(startMins + 90, 28 * 60);
}

export function useScheduleData(
  attractions: FestivalLineupEntry[]
): ScheduleData | null {
  return useMemo(() => {
    // Filter to entries that have both stage and set_time
    const eligible = attractions.filter((a) => a.stage && a.set_time);
    if (eligible.length < 5) return null;

    // Group by stage+time to detect collaborative sets
    const groupKey = (a: FestivalLineupEntry) => `${a.stage}|${a.set_time}`;
    const groups = new Map<string, FestivalLineupEntry[]>();
    for (const a of eligible) {
      const key = groupKey(a);
      const list = groups.get(key) || [];
      list.push(a);
      groups.set(key, list);
    }

    // Count acts, track earliest start, and count known artists per stage
    const stageCounts = new Map<string, number>();
    const stageEarliestStart = new Map<string, number>();
    const stageKnownScore = new Map<string, number>();
    for (const a of eligible) {
      const s = a.stage!;
      stageCounts.set(s, (stageCounts.get(s) || 0) + 1);
      const start = timeToScheduleMinutes(a.set_time!);
      const prev = stageEarliestStart.get(s);
      if (prev === undefined || start < prev) {
        stageEarliestStart.set(s, start);
      }
      // Score: spotify+image=3, spotify=2, image=1 (activities score 0)
      const known = isActivity(a) ? 0 : ((a.spotify_id ? 2 : 0) + (a.image_url ? 1 : 0));
      stageKnownScore.set(s, (stageKnownScore.get(s) || 0) + known);
    }

    // Sort stages: earliest start → most acts → most known artists
    const stages = [...stageCounts.entries()]
      .sort((a, b) => {
        const startDiff = (stageEarliestStart.get(a[0]) ?? 0) - (stageEarliestStart.get(b[0]) ?? 0);
        if (startDiff !== 0) return startDiff;
        const countDiff = b[1] - a[1];
        if (countDiff !== 0) return countDiff;
        return (stageKnownScore.get(b[0]) ?? 0) - (stageKnownScore.get(a[0]) ?? 0);
      })
      .map(([name]) => name);

    const stageIndex = new Map(stages.map((s, i) => [s, i]));

    // Main stages: ≥2 acts, capped at 8
    const mainStages = stages
      .filter((s) => (stageCounts.get(s) || 0) >= 2)
      .slice(0, 8);

    // Build preliminary slots (one per group) to get start times per stage
    const preliminary: {
      key: string;
      attractions: FestivalLineupEntry[];
      startMinutes: number;
      stage: string;
    }[] = [];

    for (const [key, group] of groups) {
      const first = group[0];
      preliminary.push({
        key,
        attractions: group,
        startMinutes: timeToScheduleMinutes(first.set_time!),
        stage: first.stage!,
      });
    }

    // Group preliminary by stage for duration inference
    const byStage = new Map<string, typeof preliminary>();
    for (const p of preliminary) {
      const list = byStage.get(p.stage) || [];
      list.push(p);
      byStage.set(p.stage, list);
    }

    // Build final slots with inferred end times
    const slots: ScheduleSlot[] = [];
    for (const p of preliminary) {
      const sameStage = byStage.get(p.stage) || [];
      const first = p.attractions[0];

      let endMinutes: number;
      if (first.set_end_time) {
        endMinutes = timeToScheduleMinutes(first.set_end_time);
      } else {
        endMinutes = inferEndMinutes(p.startMinutes, sameStage);
      }

      // Ensure minimum 15min block height for visibility
      const duration = Math.max(endMinutes - p.startMinutes, 15);

      slots.push({
        attraction: first,
        attractions: p.attractions,
        startMinutes: p.startMinutes,
        endMinutes: p.startMinutes + duration,
        durationMinutes: duration,
        stageIndex: stageIndex.get(p.stage) ?? 0,
      });
    }

    // Calculate day bounds (round to 30min)
    const allStarts = slots.map((s) => s.startMinutes);
    const allEnds = slots.map((s) => s.endMinutes);
    const dayStartMinutes = Math.floor(Math.min(...allStarts) / 30) * 30;
    const dayEndMinutes = Math.ceil(Math.max(...allEnds) / 30) * 30;

    return { stages, mainStages, slots, dayStartMinutes, dayEndMinutes };
  }, [attractions]);
}
